import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, CheckCircle, Zap, Shield, Users, Home as HomeIcon, Truck, Building2, Wrench, ShoppingCart, Hospital, Fuel, Hotel, Car } from 'lucide-react';

const HomePage = () => {
  const services = [
    {
      icon: <HomeIcon size={32} />,
      title: 'Home EV Charging',
      description: 'Convenient, safe charging solutions for homes and apartments.',
      link: '/services#residential'
    },
    {
      icon: <Zap size={32} />,
      title: 'Portable EV Chargers',
      description: 'Flexible charging anywhere using compatible South African sockets.',
      link: '/products#portable'
    },
    {
      icon: <Building2 size={32} />,
      title: 'Commercial Charging Infrastructure',
      description: 'Scalable EV charging for public and private environments.',
      link: '/services#commercial'
    },
    {
      icon: <Truck size={32} />,
      title: 'Fleet Charging Solutions',
      description: 'Smart charging systems for businesses transitioning to electric fleets.',
      link: '/services#fleet'
    },
    {
      icon: <Wrench size={32} />,
      title: 'Maintenance & Support',
      description: 'Ongoing technical support, diagnostics, and charger servicing.',
      link: '/services#maintenance'
    }
  ];

  const benefits = [
    'Smart & scalable EV charging solutions',
    'Residential and commercial installations',
    'Portable charging solutions for flexible mobility',
    'SABS-aligned equipment and safety-focused installations',
    'Designed for South African power conditions',
    'Professional support and after-sales service'
  ];

  const howItWorks = [
    {
      step: '01',
      title: 'Consultation',
      description: 'We discuss your charging needs and assess your requirements.'
    },
    {
      step: '02',
      title: 'Site Assessment',
      description: 'Our experts evaluate your electrical setup and recommend solutions.'
    },
    {
      step: '03',
      title: 'Installation',
      description: 'Professional installation by certified technicians following SABS standards.'
    },
    {
      step: '04',
      title: 'Ongoing Support',
      description: 'Continuous technical support and maintenance services.'
    }
  ];

  const industries = [
    { icon: <ShoppingCart size={32} />, name: 'Shopping Centres' },
    { icon: <Hospital size={32} />, name: 'Hospitals' },
    { icon: <Fuel size={32} />, name: 'Fuel Stations' },
    { icon: <Building2 size={32} />, name: 'Office Parks' },
    { icon: <Hotel size={32} />, name: 'Hospitality' },
    { icon: <Truck size={32} />, name: 'Fleet Operators' },
    { icon: <HomeIcon size={32} />, name: 'Residential Estates' },
    { icon: <Car size={32} />, name: 'Dealerships' }
  ];

  return (
    <div className="min-h-screen">
      <section 
        data-testid="hero-section"
        className="relative min-h-[90vh] flex items-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(to right, rgba(10, 61, 46, 0.8), rgba(10, 61, 46, 0.4)), url('https://static.prod-images.emergentagent.com/jobs/fc6e7ca9-8e07-44ba-abc5-d4f04b264f75/images/8601c74b718b6f91938afb6cb887981886e5ea5004698db53e2b03ec6eda7f7b.png')`
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="text-5xl sm:text-6xl font-bold tracking-tighter text-white mb-6 font-['Outfit']">
              Powering South Africa's Electric Future
            </h1>
            <p className="text-lg text-white/90 mb-8 leading-relaxed">
              Smart EV charging solutions for homes, businesses, fleets, and public infrastructure.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                data-testid="hero-get-quote-button"
                className="px-8 py-4 bg-[#CDFF00] text-[#0A3D2E] rounded-full font-semibold text-base hover:bg-[#B8E600] transition-colors focus:ring-4 focus:ring-[#CDFF00] focus:outline-none inline-flex items-center justify-center"
              >
                Get a Quote <ChevronRight size={20} className="ml-2" />
              </Link>
              <button
                onClick={() => window.location.href = 'https://wa.me/27780706701'}
                data-testid="hero-whatsapp-button"
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 rounded-full font-semibold text-base hover:bg-white/20 transition-colors inline-flex items-center justify-center"
              >
                Speak on WhatsApp
              </button>
              <Link
                to="/contact"
                data-testid="hero-site-assessment-button"
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 rounded-full font-semibold text-base hover:bg-white/20 transition-colors inline-flex items-center justify-center"
              >
                Book a Site Assessment
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section data-testid="value-proposition-section" className="py-24 bg-[#F4F7F5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-[#0A3D2E] mb-6 font-['Outfit']">
              Why Green Circuit EV?
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit) => (
              <motion.div
                key={benefit}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="flex items-start space-x-3 bg-white p-6 rounded-2xl border border-[#E2E8F0] hover:shadow-lg hover:-translate-y-1 transition-all"
              >
                <CheckCircle size={24} className="text-[#CDFF00] flex-shrink-0 mt-1" />
                <p className="text-base text-[#4A5D54]">{benefit}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="services-section" className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-[#0A3D2E] mb-4 font-['Outfit']">
              Our Services
            </h2>
            <p className="text-base text-[#4A5D54] max-w-2xl mx-auto">
              Comprehensive EV charging solutions tailored to your needs.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, serviceIndex) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <Link
                  to={service.link}
                  data-testid={`service-card-${serviceIndex}`}
                  className="block bg-white p-8 rounded-2xl border border-[#E2E8F0] hover:shadow-lg hover:-translate-y-1 transition-all group h-full"
                >
                  <div className="w-16 h-16 bg-[#E9F0EC] rounded-2xl flex items-center justify-center mb-6 text-[#0A3D2E] group-hover:bg-[#0A3D2E] group-hover:text-[#CDFF00] transition-colors">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-medium text-[#0A3D2E] mb-3 font-['Outfit']">{service.title}</h3>
                  <p className="text-base text-[#4A5D54] mb-4">{service.description}</p>
                  <span className="text-[#0A3D2E] font-semibold inline-flex items-center group-hover:text-[#CDFF00]">
                    Learn More <ChevronRight size={18} className="ml-1" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="how-it-works-section" className="py-24 bg-[#F4F7F5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-[#0A3D2E] mb-4 font-['Outfit']">
              How It Works
            </h2>
            <p className="text-base text-[#4A5D54] max-w-2xl mx-auto">
              Our simple 4-step process to get you charging.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((step) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="text-[#CDFF00] text-6xl font-bold mb-4 font-['Outfit']">{step.step}</div>
                <h3 className="text-2xl font-medium text-[#0A3D2E] mb-3 font-['Outfit']">{step.title}</h3>
                <p className="text-base text-[#4A5D54]">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="featured-products-section" className="py-24 bg-[#F4F7F5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-[#0A3D2E] mb-4 font-['Outfit']">
              Featured Products
            </h2>
            <p className="text-base text-[#4A5D54] max-w-2xl mx-auto">
              Professional EV charging solutions with advanced features for home and business use.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl overflow-hidden border border-[#E2E8F0] hover:shadow-lg hover:-translate-y-1 transition-all"
            >
              <div className="aspect-square bg-white overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_ev-charging-sa/artifacts/jrv4yik5_groundless%20worry%20free%20charging.jpeg"
                  alt="Portable 3 kW EV Charger"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-[#0A3D2E] mb-2 font-['Outfit']">
                  Portable 3 kW EV Charger
                </h3>
                <p className="text-base text-[#4A5D54] mb-3">
                  Groundless and worry-free charging with smart scheduling features. Ideal for home use and travel.
                </p>
                <ul className="space-y-2 mb-4 text-sm text-[#4A5D54]">
                  <li className="flex items-start">
                    <Zap size={16} className="text-[#CDFF00] mr-2 flex-shrink-0 mt-0.5" />
                    <span>Schedule off-peak charging (1-6 hour reservations)</span>
                  </li>
                  <li className="flex items-start">
                    <Zap size={16} className="text-[#CDFF00] mr-2 flex-shrink-0 mt-0.5" />
                    <span>LCD display with charging statistics</span>
                  </li>
                </ul>
                <Link
                  to="/products"
                  className="inline-flex items-center text-[#0A3D2E] font-semibold hover:text-[#CDFF00] transition-colors"
                >
                  View Details <ChevronRight size={18} className="ml-1" />
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl overflow-hidden border border-[#E2E8F0] hover:shadow-lg hover:-translate-y-1 transition-all"
            >
              <div className="aspect-square bg-white overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_ev-charging-sa/artifacts/wv9h0fam_Portable%20EV%20Charger.jpeg"
                  alt="Portable 7 kW Smart Charger"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-[#0A3D2E] mb-2 font-['Outfit']">
                  Portable 7 kW Smart Charger
                </h3>
                <p className="text-base text-[#4A5D54] mb-3">
                  Complete kit with accessories, mobile app connectivity, and smart scheduling features.
                </p>
                <ul className="space-y-2 mb-4 text-sm text-[#4A5D54]">
                  <li className="flex items-start">
                    <Zap size={16} className="text-[#CDFF00] mr-2 flex-shrink-0 mt-0.5" />
                    <span>Schedule off-peak charging (1-6 hour reservations)</span>
                  </li>
                  <li className="flex items-start">
                    <Zap size={16} className="text-[#CDFF00] mr-2 flex-shrink-0 mt-0.5" />
                    <span>LCD display with charging statistics</span>
                  </li>
                </ul>
                <Link
                  to="/products"
                  className="inline-flex items-center text-[#0A3D2E] font-semibold hover:text-[#CDFF00] transition-colors"
                >
                  View Details <ChevronRight size={18} className="ml-1" />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section data-testid="industries-section" className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-[#0A3D2E] mb-4 font-['Outfit']">
              Industries We Serve
            </h2>
            <p className="text-base text-[#4A5D54] max-w-2xl mx-auto">
              Providing EV charging solutions across diverse sectors.
            </p>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {industries.map((industry) => (
              <motion.div
                key={industry.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                viewport={{ once: true }}
                className="flex flex-col items-center justify-center bg-white p-8 rounded-2xl border border-[#E2E8F0] hover:shadow-lg hover:-translate-y-1 transition-all"
              >
                <div className="text-[#0A3D2E] mb-4">{industry.icon}</div>
                <p className="text-sm font-medium text-[#0A3D2E] text-center">{industry.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="sustainability-section" className="py-24 bg-[#0A3D2E] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight mb-6 font-['Outfit']">
                Smart Charging Solutions
              </h2>
              <p className="text-lg text-white/90 mb-6 leading-relaxed">
                Our portable EV chargers come with everything you need for convenient, reliable charging at home, work, or on the road.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center space-x-3">
                  <CheckCircle size={20} className="text-[#CDFF00] flex-shrink-0" />
                  <span>Complete charging kit with accessories</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle size={20} className="text-[#CDFF00] flex-shrink-0" />
                  <span>Mobile app connectivity for monitoring</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle size={20} className="text-[#CDFF00] flex-shrink-0" />
                  <span>Industrial socket support</span>
                </li>
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_ev-charging-sa/artifacts/wv9h0fam_Portable%20EV%20Charger.jpeg"
                alt="Portable EV Charging Solutions"
                className="rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section data-testid="cta-banner-section" className="py-24 bg-gradient-to-r from-[#E9F0EC] to-[#F4F7F5]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-[#0A3D2E] mb-6 font-['Outfit']">
              Ready to Install EV Charging?
            </h2>
            <p className="text-lg text-[#4A5D54] mb-8">
              Get expert advice and a customised charging solution for your home or business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                data-testid="cta-request-quote-button"
                className="px-8 py-4 bg-[#0A3D2E] text-white rounded-full font-semibold text-base hover:bg-[#07291F] transition-colors focus:ring-4 focus:ring-[#CDFF00] focus:outline-none inline-flex items-center justify-center"
              >
                Request a Quote
              </Link>
              <button
                onClick={() => window.location.href = 'https://wa.me/27780706701'}
                data-testid="cta-whatsapp-button"
                className="px-8 py-4 bg-[#CDFF00] text-[#0A3D2E] rounded-full font-semibold text-base hover:bg-[#B8E600] transition-colors inline-flex items-center justify-center"
              >
                WhatsApp Us
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
